import { Stack, styled, SxProps } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { EntityBase } from '@provision/entity-types';
import {
  cloneElement,
  MouseEvent,
  ReactElement,
  ReactNode,
  RefObject,
  UIEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useResizable } from 'react-resizable-layout';

import { DynamicIcon, NamedLayoutBlock, Tooltip, VerticalSeparator } from 'src/components';
import { ModalContainer } from 'src/components/ModalContainer';

import {
  CustomViewAttribute,
  CustomViewEntity,
  CustomViewFormFields,
  CustomViewFormModel,
  CustomViewSorting,
  useCustomViewEntityConfig,
} from 'src/graphql/entities/custom-view';
import { mapSortAttributeToFormInterface } from 'src/graphql/entities/custom-view/custom-view-config/utils';
import { EntityNames } from 'src/graphql/types';
import { CustomViewsEntitiesTable } from 'src/pages/CustomViews';
import type { TableSelectionState } from 'src/pages/CustomViews/custom-views-entities-table/custom-views-entities-table';

import { CustomViewsColumnsSelectorForm } from 'src/pages/CustomViews/custom-views-form-view';
import { CustomViewsActionsHeader } from 'src/pages/CustomViews/custom-views-form-view/custom-views-actions-header';
import { CustomViewsQueryBuilder } from 'src/pages/CustomViews/custom-views-form-view/custom-views-query-builder';
import { MobileFilterWizard } from 'src/pages/CustomViews/custom-views-form-view/mobile-filter-wizard';
import { preventEnterSubmit } from 'src/pages/CustomViews/utils';
import useCustomViewsStore from 'src/store/custom-views';
import { useScreenDimensions } from 'src/utils/system';
import { CreateButtonProps } from './custom-views-selected-view/components/create-button';

const LEFT_PANEL_MIN_WIDTH = 300;
const LEFT_PANEL_WIDTH = 600;

const NO_COLUMNS: CustomViewAttribute[] = [];

const RecordsListWrapper = styled(NamedLayoutBlock, {
  name: 'RecordsListWrapper',
})(({ theme }) => ({
  flex: 1,
  zIndex: theme.zIndex.app.ServiceDeskConversations,
}));

export const RecordsListView = styled(Stack, {
  name: 'RecordsListView',
  shouldForwardProp: (prop) =>
    !(
      [
        'isResponsiveLayoutOff',
        'customWidth',
        'disableMaxWidth',
        'panelOwnsWidth',
      ] as PropertyKey[]
    ).includes(prop),
})<{
  isResponsiveLayoutOff: boolean;
  customWidth: number;
  disableMaxWidth?: boolean;
  panelOwnsWidth?: boolean;
}>(({ theme, isResponsiveLayoutOff, customWidth, disableMaxWidth, panelOwnsWidth }) => ({
  position: 'relative',
  // in panelOwnsWidth mode the right panel is the fixed-width side, so the grid
  // has to be the flexible one — it can't also claim a fixed customWidth
  minWidth: panelOwnsWidth ? LEFT_PANEL_MIN_WIDTH : 0,
  width: (() => {
    if (panelOwnsWidth) return 'auto';
    return isResponsiveLayoutOff ? 'calc(100vw - 50rem)' : customWidth;
  })(),
  flex: panelOwnsWidth || isResponsiveLayoutOff ? '1 1 auto' : undefined,
  height: '100%',
  ...(isResponsiveLayoutOff || disableMaxWidth || panelOwnsWidth
    ? {}
    : {
        [theme.breakpoints.down(1500)]: {
          maxWidth: '40%',
        },

        [theme.breakpoints.down(2000)]: {
          maxWidth: '50%',
        },

        [theme.breakpoints.up(2000)]: {
          maxWidth: '60%',
        },
      }),
}));

interface RecordsViewLayoutProps<T extends EntityBase> {
  records: T[];
  isRecordsLoading?: boolean;
  isRecordsLoadingMore?: boolean;
  entityName: CustomViewEntity;
  attributesList: CustomViewAttribute[];
  onRecordClose?: () => void;
  onRecordClick?: (id: T['id'], record: T, event?: MouseEvent) => void;
  onRecordOpenFullPage?: (id: T['id'], record: T) => void;
  selectedRecord?: T | null;
  leftHeaderAdornment?: ReactNode;
  isQueryBuilderOpen: boolean;
  filters: CustomViewFormModel['conditions'];
  columns: CustomViewFormModel['columns'];
  sort?: CustomViewSorting | null;
  formMethods: UseFormReturn<Pick<CustomViewFormModel, CustomViewFormFields.Conditions>>;
  handleFilterChange: (filters: CustomViewFormModel['conditions']) => void;
  handleSortChange?: (sort: CustomViewSorting | null) => void;
  handleColumnUpdate: (column: CustomViewAttribute) => void;
  /** `null` drops the local column changes and goes back to the view's own columns. */
  handleColumnsUpdate: (columns: CustomViewFormModel['columns'] | null) => void;
  handleCopyClick?: () => void;
  handleUpdateOriginView?: () => void;
  toggleQueryBuilder: () => void;
  totalRecords?: number;
  handleScroll?: (e: UIEvent) => Promise<void>;
  handleSearch?: (value: string) => void;
  sx?: SxProps<Theme>;
  leftPanelWidth?: number;
  leftPanelMinWidth?: number;
  resetToDefault?: () => void;
  selectionState?: TableSelectionState;
  /** The host renders a self-sizing peek panel that owns its own width and resize handle. */
  hasDetachedPanel?: boolean;
  slots?: {
    header?: ReactNode;
    rightPanel?: ReactNode;
    createButton?: ReactElement;
    subHeader?: ReactNode;
    /** Floats over the bottom of the list panel, e.g. the bulk selection bar. */
    listOverlay?: ReactNode;
  };
  slotProps?: {
    createButton?: CreateButtonProps;
  };
}

export const RecordsGridLayout = <T extends EntityBase>({
  handleCopyClick,
  handleUpdateOriginView,
  entityName,
  records,
  isRecordsLoading,
  isRecordsLoadingMore,
  attributesList,
  selectedRecord,
  leftHeaderAdornment,
  isQueryBuilderOpen,
  filters,
  columns,
  formMethods,
  sort,
  totalRecords,
  slots,
  slotProps,
  sx,
  leftPanelWidth,
  leftPanelMinWidth,
  resetToDefault,
  handleScroll,
  handleFilterChange,
  handleSortChange,
  handleColumnUpdate,
  handleColumnsUpdate,
  toggleQueryBuilder,
  onRecordClose,
  onRecordClick,
  onRecordOpenFullPage,
  handleSearch,
  selectionState,
  hasDetachedPanel,
}: RecordsViewLayoutProps<T>) => {
  const viewRef = useRef<HTMLDivElement | null>(null);
  const [columnsSelectorAnchorEl, setColumnsSelectorAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const { isMobile, isDesktop } = useScreenDimensions();

  const isResponsiveLayoutOff = isMobile || !selectedRecord;

  // Site's detached peek owns its own width (see SiteDetachedPanel) — the grid must not
  // also run a resizable of its own, or the two fight over the same row and both a
  // duplicate handle and an overflowing row appear. Only the host that actually renders
  // such a panel opts in; other hosts of the same entity keep the docked geometry.
  const isPanelOwnedWidthMode =
    isDesktop && !!hasDetachedPanel && !!selectedRecord && !!slots?.rightPanel;

  const { position, separatorProps, isDragging } = useResizable({
    axis: 'x',
    initial: leftPanelWidth ?? LEFT_PANEL_WIDTH,
    min: leftPanelMinWidth ?? LEFT_PANEL_MIN_WIDTH,
    containerRef: viewRef as RefObject<HTMLElement>,
    disabled: isResponsiveLayoutOff || isPanelOwnedWidthMode,
  });

  // although RecordsViewLayout is a shared component used across multiple views
  // (e.g., search, records, custom views etc), we want updates to column widths here
  // to dynamically reflect across all pages where it’s used. This ensures
  // a consistent column resizing behavior and UI experience across different views.
  // but we would need to move this outside if product changes its decision to have shared width settings
  const {
    columnWidthMap,
    updateColumnWidthMap,
    gridDensity = 'comfortable',
    setGridDensity,
  } = useCustomViewsStore();

  const { attributesMap, filterAttributesList, columnRenderersById } =
    useCustomViewEntityConfig(entityName);

  const selectedSort = useMemo(
    () => (sort ? { id: sort.attribute, desc: sort.desc } : undefined),
    [sort],
  );

  const handleSortClick = useCallback(
    (newSort: { id: string; desc: boolean } | null) => {
      if (!newSort && !sort) {
        handleSortChange?.(null);
        return;
      }

      const attributeKey = newSort?.id || sort!.attribute;
      const desc = newSort?.desc ?? !sort?.desc;
      const attribute = attributesMap.get(attributeKey)!;

      handleSortChange?.(mapSortAttributeToFormInterface(attribute, { desc }));
    },
    [handleSortChange, sort, attributesMap],
  );

  const hideColumnsModal = useCallback(() => setColumnsSelectorAnchorEl(null), []);
  const resetColumns = useCallback(() => handleColumnsUpdate(null), [handleColumnsUpdate]);
  const isSplitMode = isDesktop && !!selectedRecord && !!slots?.rightPanel;
  // the detached-panel geometry is explicitly opted into by the host; everyone else keeps the single outer card
  const isDetachedSplitMode =
    isSplitMode && (entityName === EntityNames.Conversation || !!hasDetachedPanel);
  // every other entity keeps the old look: grid docked to the panel, rounded on its left only
  const isDockedSplitMode = !!isSplitMode && !isDetachedSplitMode;

  return (
    <Stack
      data-name="custom-views-records-grid-layout"
      data-split-mode={isDetachedSplitMode || undefined}
      direction="row"
      flexDirection="row"
      sx={{
        position: 'relative',
        height: '100%',
        flex: '1',
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      <RecordsListView
        isResponsiveLayoutOff={isResponsiveLayoutOff}
        disableMaxWidth={!!leftPanelWidth || isPanelOwnedWidthMode}
        customWidth={position}
        panelOwnsWidth={isPanelOwnedWidthMode}
        ref={viewRef}
        spacing={0.5}
        sx={sx}
        data-testid="records-list-panel"
      >
        <RecordsListWrapper
          subHeader={slots?.subHeader}
          customTitle={slots?.header}
          leftHeaderAdornment={leftHeaderAdornment}
          rightHeaderAdornment={
            <Stack
              flexDirection="row"
              alignItems="center"
              gap={2}
              position="relative"
              height="100%"
            >
              {!!slots?.createButton &&
                cloneElement(slots.createButton, {
                  ...(slotProps?.createButton ?? {}),
                })}
              {!!selectedRecord && !isMobile && (
                <Tooltip title="Close opened record">
                  <DynamicIcon
                    name="left_panel_open"
                    sx={{ cursor: 'pointer' }}
                    onClick={onRecordClose}
                  />
                </Tooltip>
              )}
            </Stack>
          }
          BlockContentProps={{ sx: { padding: 0 } }}
          framed={isDetachedSplitMode}
          leftRoundedOnly={isDockedSplitMode}
        >
          <Stack
            padding={isMobile ? '0.3rem' : '0.3rem 0.8rem'}
            borderBottom={(theme) => `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`}
          >
            <CustomViewsActionsHeader
              gridDensity={gridDensity}
              showModifiedViewActions={!!resetToDefault}
              setGridDensity={setGridDensity}
              resetToDefault={resetToDefault}
              handleCopyClick={handleCopyClick}
              handleUpdateClick={handleUpdateOriginView}
              handleFilterChange={(rules) => {
                handleFilterChange({
                  ...filters,
                  rules,
                });
              }}
              conditions={filters?.rules}
              toggleQueryBuilder={toggleQueryBuilder}
              setColumnsSelectorAnchorEl={setColumnsSelectorAnchorEl}
              deleteCondition={(id: string) =>
                handleFilterChange({
                  ...filters,
                  rules: filters?.rules?.filter((rule) => rule.id !== id) ?? [],
                })
              }
              handleSearch={handleSearch}
            />
            {/* Stays mounted while a reset clears the columns, or the open dialog would close and reopen. */}
            <CustomViewsColumnsSelectorForm
              onSelect={handleColumnUpdate}
              selectedColumns={columns ?? NO_COLUMNS}
              open={Boolean(columnsSelectorAnchorEl)}
              onReorder={handleColumnsUpdate}
              onReset={resetColumns}
              onClose={hideColumnsModal}
              attributes={attributesList}
            />
            {isMobile ? (
              <FormProvider {...formMethods}>
                <MobileFilterWizard
                  open={isQueryBuilderOpen}
                  onClose={toggleQueryBuilder}
                  name={CustomViewFormFields.Conditions}
                  filterAttributesList={filterAttributesList}
                  onChange={handleFilterChange}
                />
              </FormProvider>
            ) : (
              <ModalContainer
                open={isQueryBuilderOpen}
                onClose={toggleQueryBuilder}
                title="Set filters"
                onKeyDown={preventEnterSubmit}
                size="large"
                data-testid="set-filters-modal"
                actions={[
                  {
                    label: 'Done',
                    kind: 'primary',
                    onClick: toggleQueryBuilder,
                    testId: 'set-filters-save',
                  },
                ]}
              >
                {/* A builder still mounted after close pushes its value back into the grid state and loops with the form reset in useCustomViewState. */}
                {isQueryBuilderOpen ? (
                  <FormProvider {...formMethods}>
                    <CustomViewsQueryBuilder
                      filterAttributesList={filterAttributesList}
                      onChange={handleFilterChange}
                      name={CustomViewFormFields.Conditions}
                    />
                  </FormProvider>
                ) : null}
              </ModalContainer>
            )}
          </Stack>
          <CustomViewsEntitiesTable<T>
            entityName={entityName}
            onScroll={handleScroll}
            rows={records}
            isLoading={isRecordsLoading}
            isLoadingMore={isRecordsLoadingMore}
            selectedColumns={columns}
            selectedId={selectedRecord?.id as string}
            onClick={onRecordClick}
            totalRecords={totalRecords ?? 0}
            columnsWidthMap={columnWidthMap}
            setColumnWidth={updateColumnWidthMap}
            sort={selectedSort}
            onSortChange={handleSortChange ? handleSortClick : undefined}
            gridDensity={gridDensity}
            selectionState={selectionState}
            customAttributeRenderers={columnRenderersById}
            hasDetachedPanel={hasDetachedPanel}
            onRecordOpenFullPage={onRecordOpenFullPage}
          />
        </RecordsListWrapper>
        {slots?.listOverlay ?? null}
      </RecordsListView>
      {isSplitMode && !isPanelOwnedWidthMode ? (
        <VerticalSeparator {...separatorProps} sx={{ margin: '0.5rem 0' }} />
      ) : null}

      {slots?.rightPanel ?? null}
    </Stack>
  );
};
